import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api/client';
import { queryKeys } from '@/lib/queryKeys';

/**
 * 바로가기 링크(`/v1/shortcuts`) — 회사 홈페이지 · 블로그 · 그룹웨어처럼 이 시스템 <b>바깥</b>으로 나가는 주소.
 *
 * 헤더 드롭다운(`Navigation`)과 환경설정 화면(`ShortcutLinksPage`)이 같은 데이터를 본다.
 * 그래서 훅을 `pages/settings` 가 아니라 `lib` 에 둔다 — 헤더가 설정 화면 폴더를 향해 import 하면
 * 나중에 그 화면을 옮길 때 헤더가 함께 깨진다.
 *
 * 조회는 로그인한 모든 사용자에게 열려 있고, 저장만 `settings_shortcuts` 편집 권한을 요구한다.
 */

export type ShortcutLink = {
  label: string;
  url: string;
  /** 목록에서 이름 아래 작게 붙는 한 줄 설명. 없으면 null 로 내려온다. */
  description?: string | null;
};

export function useShortcutLinks() {
  return useQuery({
    queryKey: queryKeys.shortcutLinks,
    queryFn: () => apiFetch<ShortcutLink[]>('/v1/shortcuts'),
    // 몇 달에 한 번 바뀌는 값인데 화면 전환마다 헤더가 다시 물어보면 요청만 는다.
    staleTime: 10 * 60 * 1000,
  });
}

export function useSaveShortcutLinks() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (links: ShortcutLink[]) =>
      apiFetch<ShortcutLink[]>('/v1/shortcuts', { method: 'PUT', body: links }),
    onSuccess: (saved) => {
      // 서버가 돌려준 값을 그대로 캐시에 넣는다 — 정규화(공백 제거·빈 줄 제거)가 화면에도 보여야 하고,
      // 헤더 드롭다운이 같은 캐시를 보므로 저장 즉시 반영된다.
      queryClient.setQueryData(queryKeys.shortcutLinks, saved);
    },
  });
}
